import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MuiThemeProvider } from '@material-ui/core';
import classNames from 'classnames';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import Modal from 'Components/modals/modal/modal';
import CreateServiceLoader from 'Components/signup-followup-details/create-service-loader';
import useMobileDetect from 'Hooks/useMobileDetect';
import useWindowSize from 'Hooks/useWindowSize';
import totangoTheme from 'Theme/totangoTheme';
import { isFileExists } from 'Utils/api/uploads';
import { CAPTCHA_FF } from 'Utils/constants';
import { delay, isLocal, isTestEnv } from 'Utils/utils';

import SignupError from './steps/signup-error';
import { SIGNUP_METHOD_TYPES } from './tracking/google-analytics';
import {
	checkSignup880FeatureEnabled,
	createAccount,
	ERROR_TYPE_MAP,
	generateAuthenticationCode as generateAuthenticationCodeApi,
	trackContact,
	waitForProgress,
} from './utils/api';
import { dataCenters } from './utils/constants';
import {
	DEFAULT_ROUTE,
	DEFAULT_SIGNUP_EDITION,
	getBackgroundStyle,
	onAccountCreatedUtil,
	trackExternal,
	uploadToUploadcareFromUrl,
	utmParams,
} from './utils/utils';
import LoginCarousel from './login-carousel';
import MarketingIframe from './marketing-iframe';

import './new-signup-page.less';

const { searchParams } = new URL(window.location.href);
const backGroundPicture = searchParams.get('background');
const signupEmail = searchParams.get('signup_email');
const firstNameParam = searchParams.get('first_name');
const oauthIssuerParam = searchParams.get('oauth_issuer');
const lastNameParam = searchParams.get('last_name');
const passwordParam = searchParams.get('password');
const signupCode = searchParams.get('signup_code');
const EU_OFFSETS = [0, -60, -120, -180];
const isEuTimeZone = EU_OFFSETS.includes(new Date().getTimezoneOffset());
const shouldShowDataCenterOptions = !isTestEnv() && !isLocal() && isEuTimeZone;
const editionParam = searchParams.get('edition');
const chosenDataCenter = includes(window.location.href, '/t11/') ? 'eu' : 'us';

const NewSignupPage = () => {
	const history = useHistory();
	const { isMobile } = useMobileDetect();
	const windowSize = useWindowSize();
	const [backgroundImage, setBackgroundImage] = useState('');
	const [isTestingBg, setIsTestingBg] = useState(!!utmParams.utmContent);
	const [email, setEmail] = useState(signupEmail || '');
	const [password, setPassword] = useState(passwordParam || '');
	const [isLoading, setIsLoading] = useState(false);
	const [firstName, setFirstName] = useState(firstNameParam || '');
	const [lastName, setLastName] = useState(lastNameParam || '');
	const [profileImageUrl, setProfileImageUrl] = useState('');
	const [emailHash, setEmailHash] = useState('');
	const [oauthIssuer, setOauthIssuer] = useState(oauthIssuerParam);
	const [avatarId, setAvatarId] = useState('');
	const [agreedReceiveContent, setAgreedReceiveContent] = useState(false);
	const [createRequestExecuted, setCreateRequestExecuted] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [creationStatus, setCreationStatus] = useState(null);
	const [captchaEnabled, setCaptchaEnabled] = useState(null);
	const [edition] = useState(editionParam || DEFAULT_SIGNUP_EDITION);
	const [dataCenter, setDataCenter] = useState(() => {
		if (!shouldShowDataCenterOptions) {
			return null;
		}
		return isEuTimeZone ? dataCenters.eu : dataCenters.us;
	});

	const isTrialEdition = edition === DEFAULT_SIGNUP_EDITION;

	useEffect(() => {
		if (!isTrialEdition) {
			searchParams.set('edition', DEFAULT_SIGNUP_EDITION);
			const newSearch = searchParams.toString();
			window.location.search = newSearch;
		}
	}, [isTrialEdition]);

	useEffect(() => {
		const fetchFeatureEnabled = async () => {
			try {
				const featureEnabled = await checkSignup880FeatureEnabled(CAPTCHA_FF);
				setCaptchaEnabled(featureEnabled);
			} catch (err) {
				console.error('Failed to fetch feature enabled', err);
			}
		};

		fetchFeatureEnabled();
	}, []);

	const isRecaptchaFeatureEnabled = captchaEnabled === true || captchaEnabled?.data === true;

	useEffect(() => {
		setEmail(() => (email ? email.trim() : email));
	}, [email]);

	const backGroundStyle = useMemo(
		() => getBackgroundStyle(backgroundImage, windowSize, isTestingBg, backGroundPicture),
		[backgroundImage, isTestingBg, windowSize],
	);

	useEffect(() => {
		const checkIfUtmImageExists = async fileId => {
			const doesFileExist = await isFileExists(fileId);
			if (doesFileExist) {
				setBackgroundImage(fileId);
			}
			setIsTestingBg(false);
		};
		if (utmParams.utmContent) {
			checkIfUtmImageExists(utmParams.utmContent);
		}
	}, []);

	useEffect(() => {
		if (profileImageUrl) {
			uploadToUploadcareFromUrl(profileImageUrl, setAvatarId);
		}
	}, [profileImageUrl]);

	useEffect(() => {
		if (!signupCode) {
			trackExternal(activeStep);
		}
	}, [activeStep]);

	const generateAuthenticationCode = useCallback(
		async (userEmail, sendEmail = true) => {
			const data = await generateAuthenticationCodeApi({
				email: userEmail,
				utm_source: utmParams.utmSource,
				utm_campaign: utmParams.utmCampaign,
				utm_medium: utmParams.utmMedium,
				utm_term: utmParams.utmTerm,
				utm_content: utmParams.utmContent,
				original_path: utmParams.originalPath,
				send_email: sendEmail,
				first_name: firstName,
				last_name: lastName,
				avatar_id: avatarId,
				password,
				edition,
				oauth_issuer: oauthIssuer,
				dataCenter,
				short_signup: true,
				email_hash: emailHash,
			});
			if (data.redirect) {
				window.location.href = data.redirect;
			}
			return data;
		},
		[firstName, lastName, avatarId, password, oauthIssuer, dataCenter, emailHash, edition],
	);

	const doTrackContact = useCallback(
		async ({ serviceId, emailConfirmed = false } = {}) => {
			const emailDomain = email.split('@')[1];
			const company = emailDomain && emailDomain.split('.')[0];

			const params = {
				email,
				firstname: !isEmpty(firstName) ? firstName : undefined,
				lastname: !isEmpty(lastName) ? lastName : undefined,
				company: !isEmpty(company) ? company : undefined,
				service_id__c: !isEmpty(serviceId) ? serviceId : undefined,
				user_confirmed_email: emailConfirmed.toString(),
				password_set: (activeStep > 1).toString(),
			};
			if (agreedReceiveContent) {
				params.hs_legal_basis = 'Freely given consent from contact';
			}
			try {
				await trackContact(params);
			} catch (err) {
				console.error('tracking err', err);
			}
		},
		[email, firstName, lastName, activeStep, agreedReceiveContent],
	);

	const handleRedirect = useCallback(() => {
		history.push('/downloadApp');
	}, [history]);

	const onAccountCreated = useCallback(
		async newService => {
			onAccountCreatedUtil(
				newService,
				isMobile,
				handleRedirect,
				oauthIssuer ? SIGNUP_METHOD_TYPES.SSO : SIGNUP_METHOD_TYPES.STANDARD,
			);
		},
		[handleRedirect, isMobile, oauthIssuer],
	);

	const onCreationFailed = useCallback(error => {
		setErrorMessage(error);
		setIsError(true);
		setIsLoading(false);
	}, []);

	const onResendLink = useCallback(() => {
		generateAuthenticationCode(email);
	}, [email, generateAuthenticationCode]);

	useEffect(() => {
		if (creationStatus) {
			const { status, data } = creationStatus;
			switch (status) {
				case 'done':
					onAccountCreated(data);
					break;
				case 'failed':
					onCreationFailed(data);
					break;
				case 'user_exists':
					window.location.href = isLocal()
						? `https://localhost:8443/debug.html#/${DEFAULT_ROUTE}`
						: `https://${window.location.host}#/${DEFAULT_ROUTE}`;
					break;
				case 'in_progress':
					waitForProgress(data, email, signupCode, setCreationStatus);
					break;
				default:
					break;
			}
		}
	}, [email, creationStatus, onAccountCreated, onCreationFailed]);

	const tryCreateAccount = useCallback(async creationParams => {
		let attempt = 1;
		while (attempt < 4) {
			try {
				const creationResponse = await createAccount(creationParams);
				return creationResponse;
			} catch (error) {
				const message = error?.response?.data?.error;
				if (ERROR_TYPE_MAP[message]) {
					throw error;
				}
				await delay(1000);
				attempt += 1;
			}
		}
		return null;
	}, []);

	const doCreateAccount = useCallback(async () => {
		const params = {
			code: signupCode,
			timezone: new Date().toString().match(/([-\+][0-9]+)\s/)[1], //i.e: +0400
			username: email,
			firstName,
			lastName,
			password,
			location: chosenDataCenter,
			short_signup: true,
			oauthIssuer,
			edition,
			...utmParams,
		};
		if (oauthIssuer && avatarId.length) {
			params.avatarId = avatarId;
		}
		setIsLoading(true);

		try {
			const creationResponse = await tryCreateAccount(params);
			if (creationResponse.service_id) {
				await doTrackContact({ serviceId: creationResponse.service_id, emailConfirmed: true });
				onAccountCreated(creationResponse);
			} else {
				setCreationStatus(creationResponse);
			}
		} catch (error) {
			onCreationFailed(error);
		}
	}, [
		email,
		firstName,
		lastName,
		password,
		oauthIssuer,
		avatarId,
		doTrackContact,
		onAccountCreated,
		onCreationFailed,
		tryCreateAccount,
		edition,
	]);

	useEffect(() => {
		if (signupCode) {
			doTrackContact({ emailConfirmed: true });
			doCreateAccount();
			setCreateRequestExecuted(true);
		}
	}, [doCreateAccount, doTrackContact]);

	const shouldRenderSignupModal = isTrialEdition && !createRequestExecuted;

	return (
		<>
			<MuiThemeProvider theme={totangoTheme}>
				<div className="signup-page">
					{shouldRenderSignupModal ? (
						<Modal
							style={backGroundStyle}
							isOpen
							hideFooter
							hideHeader
							disableBackdropClick
							showHeaderCloseButton={false}
							className="signup-form-modal new-signup"
							message={
								<>
									<div
										className={classNames('left-signup-side-container', {
											captcha: isRecaptchaFeatureEnabled,
										})}
									>
										<div className="image-logo-wrapper">
											<img
												src="/img/logo/totango-dark.png"
												className="totango-name-logo-stepper"
												alt="Totango"
											/>
										</div>
										<LoginCarousel
											setActiveStep={setActiveStep}
											activeStep={activeStep}
											email={email}
											setEmail={setEmail}
											setFirstName={setFirstName}
											setLastName={setLastName}
											setProfileImageUrl={setProfileImageUrl}
											setOauthIssuer={setOauthIssuer}
											password={password}
											setPassword={setPassword}
											setAgreedReceiveContent={setAgreedReceiveContent}
											agreedReceiveContent={agreedReceiveContent}
											dataCenter={dataCenter}
											setDataCenter={setDataCenter}
											shouldShowDataCenterOptions={shouldShowDataCenterOptions}
											generateAuthenticationCode={generateAuthenticationCode}
											doTrackContact={doTrackContact}
											oauthIssuer={oauthIssuer}
											firstName={firstName}
											lastName={lastName}
											setEmailHash={setEmailHash}
											captchaEnabled={captchaEnabled}
										/>
									</div>
									<div className="right-signup-side-container">
										<MarketingIframe />
									</div>
								</>
							}
						/>
					) : (
						<>
							{isLoading && <CreateServiceLoader style={backGroundStyle} />}
							{isError && (
								<SignupError
									errorType={ERROR_TYPE_MAP[errorMessage]}
									onResendLink={onResendLink}
									signupEmail={signupEmail}
								/>
							)}
						</>
					)}
				</div>
			</MuiThemeProvider>
		</>
	);
};

export default NewSignupPage;
